import { type ReactNode } from 'react';
import { cn } from '@/utils/tailwind.util';

/**
 * TypeBadge - Generic type badge component
 *
 * @description
 * Reusable badge component that displays a type with an icon.
 * Used by QuizTypeBadge (multiple/subjective/ox) and MaterialTypeBadge (file/youtube/website).
 *
 * Design tokens:
 * - Button variant: contained, neutral, size xs
 * - Matches Figma node pattern: Button/contained/neutral
 *
 * @example
 * ```tsx
 * // Quiz type badge
 * <TypeBadge
 *   icon={<CheckSmallCircleFillIcon size="xs" fill={Colors.Green600} />}
 *   label="객관식"
 * />
 *
 * // Material type badge
 * <TypeBadge
 *   icon={<PaperFillIcon size="2xs" fill={ICON_COLOR.neutral} />}
 *   label="파일"
 * />
 * ```
 */

interface TypeBadgeProps {
  /** Icon element to display (with size and color configured) */
  icon?: ReactNode;
  /** Text label to display */
  label: string;
  /** Optional className for customization */
  className?: string;
}

/**
 * TypeBadge component - Generic badge for displaying type information
 * Uses Button component with contained/neutral variant for consistent styling
 */
export const TypeBadge = ({
  icon,
  label,
  className
}: TypeBadgeProps) => {
  return <span className={cn('inline-flex items-center justify-center whitespace-nowrap', 'h-[30px] min-w-[30px] gap-3xs rounded-[6px] border border-opacity-assistive bg-gray-50 px-[8px]', 'text-caption-lg-semibold text-gray-700', className)} data-sentry-component="TypeBadge" data-sentry-source-file="TypeBadge.tsx">
      {icon}
      {label}
    </span>;
};